import React from 'react';
import { useEffect } from 'react';
import ApexCharts from 'apexcharts';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface OccupancyPaymentsChartProps {
  data: {
    occupancy: number;
    payments: number;
  };
}

export const OccupancyPaymentsChart: React.FC<OccupancyPaymentsChartProps> = ({ data }) => {
  const { t } = useTranslation();

  useEffect(() => {
    const options = {
      series: [data.occupancy, data.payments],
      chart: {
        height: 150,
        width: 150,
        justifyContent: 'center',
        alignItems: 'center',
        type: 'radialBar',
        sparkline: {
          enabled: true,
        },
        toolbar: {
          show: false,
        },
        background: 'transparent',
        padding: {
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
        },
        offsetX: 0,
        offsetY: -10,
      },
      plotOptions: {
        radialBar: {
          startAngle: 0,
          endAngle: 360,
          hollow: {
            size: '35%',
          },
          track: {
            background: '#f2f2f2',
            strokeWidth: '97%',
            margin: 8,
            dropShadow: {
              enabled: false,
            },
          },
          dataLabels: {
            show: false,
          },
          strokeWidth: 65,
        },
      },
      colors: ['#FCCE23', '#F9A320'],
      labels: ['Occupation', 'Payments'],
      legend: {
        show: false,
      },
      stroke: {
        lineCap: 'round',
        curve: 'smooth',
      },
    };

    const chart = new ApexCharts(document.querySelector('#chart_occupancy'), options);
    chart.render();

    return () => {
      chart.destroy();
    };
  }, [data]);

  return (
    <Box
      sx={{
        backgroundColor: 'white',
      }}
      className="rounded-lg p-4 shadow-small"
    >
      <Typography className="mb-3 font-Vollkorn text-lead1 font-bold leading-[27px] text-[#111827]">
        {t('js.features.admin.stats.dashboard.occupancy_payments_chart.title')}
      </Typography>
      <Typography
        sx={{
          '&.pdf-export': {
            marginTop: '30px',
          },
        }}
        id="chart_occupancy"
        className="vacancy-maintenance-overview flex items-center justify-center"
      />

      <Box>
        {[
          { label: 'Occupation', value: data.occupancy, color: '#FBCE26' },
          { label: 'Payments', value: data.payments, color: '#F9A320' },
        ].map((item) => (
          <Box key={item.label} className="mb-2 flex items-center">
            <Box
              sx={{ width: 12, height: 12 }}
              className="mb-2 mr-1 rounded-full"
              style={{ backgroundColor: item.color }}
            />
            <Box className="flex flex-col">
              <Typography variant="lead1" className="text-lead2 font-bold text-[#212529]">
                {item.value}%
              </Typography>
              <Typography variant="lead1" className="text-footnote1 font-medium text-[#6C757D]">
                {item.label}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
